import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { checkAHAVI, formatPriceCurrency, getCurrency, showError, showSuccess, showWarning } from 'utils/utils';
import { validate } from 'utils/validation';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { useTranslation } from 'react-i18next';
import {
  CreateProposalParams,
  DynamicProposal,
  Proposal,
  ProposalProductKey,
} from 'ts/shared/interfaces/CreateProposalParams';
import FlightProposal from './FlightProposal';
import useRecaptcha from 'hooks/useRecaptcha';
import TrnProposal from './TransferProposal';
import ILPackageProposal from './ILPackageProposal';
import Hotel2Proposal from './Hotel2Proposal';
import EditPriceModal from './EditPriceModal';
import ProposalCustomize from '../ProposalCustomize';
import ProposalEdit from '../Single/ProposalEdit';
import { createProposal } from 'services/cms';
import LoaderMain from '../../LoaderMain';
import withErrorBoundary from '../../withErrorBoundary';
import { applyProposalCurrency, checkIfProposalExpired, getActiveProposalCurrency, getProposalDescription, getReportList, resolveProposalLanguageFromBranch } from 'utils/shared/proposal';
import TicketProposal from './TicketProposal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatPriceProposal, } from 'utils/shared/utils';
import { addLogs } from 'services/user';
import { DEFAULT_CANCELLATION_DATE_DELTA, tripBuilderAdditionalInfoValidation } from 'constants/constant';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import TripBuilderAdditionalInfoModal, { TripBuilderAdditionalInfoProposalSettings, TripBuilderAdditionalInfoValues } from '../TB/TripBuilderAdditionalInfoModal';
import {
  buildTripBuilderAdditionalInfoParams,
  getDefaultTripBuilderExpireDate,
  mapProposalToTripBuilderAdditionalInfo,
} from 'utils/shared/tripBuilder';
import ProposalActions from '../ProposalActions';
import { resolveLanguageFromProposal } from 'utils/shared/tripResultBuilderPreview';
import { MainContext } from 'contexts/MainContext';
import { enqueueProposalUi, useProposalUi } from '../Assistant/proposalActions';

interface ProposalNewProps {
  onProposalCreated?: (proposal: ProposalData) => void;
  listenToAssistant?: boolean;
  tripBuilderMode?: boolean;
}

const ProposalNew: FC<ProposalNewProps> = ({
  listenToAssistant = true,
  tripBuilderMode: tripBuilderModeProp = false,
}) => {
  const { t } = useTranslation();
  const { proposal, setProposal, user } = useContext(MainContext);
  const tripBuilderMode = tripBuilderModeProp;
  const [comment, setComment] = useState('Have a nice trip!');
  const [loadingCreate, setLoadingCreate] = useState(false);
  const { handleRecapcha } = useRecaptcha();
  const [showEditPrice, setShowEditPrice] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [additionalPaymentsStr, setAdditionalPaymentsStr] = useState<string[]>([]);
  const defaultLanguage = proposal.existingProposal
    ? resolveLanguageFromProposal(proposal.existingProposal.externalParam_1)
    : resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);
  const [language, setLanguage] = useState(defaultLanguage);
  const [template, setTemplate] = useState('t1');
  const [sort, setSort] = useState('1');
  const [currency, setCurrency] = useState(() => getActiveProposalCurrency(proposal));
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [additionalInfoShowValidation, setAdditionalInfoShowValidation] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<TripBuilderAdditionalInfoValues>(() =>
    mapProposalToTripBuilderAdditionalInfo(proposal.existingProposal as any),
  );
  const additionalInfoOpenedByValidationRef = useRef(false);
  const pendingAdditionalInfoRef = useRef<TripBuilderAdditionalInfoValues | null>(null);
  const pendingShareRef = useRef<{
    channel: 'email' | 'pdf' | 'whatsapp' | 'telegram';
    email?: string;
  } | null>(null);
  const [editDate, setEditDate] = useState<Date | undefined>(undefined);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [selectedRcUniqueKey, setSelectedRcUniqueKey] = useState<string>();
  const [defaultShowCancellationDate, setDefaultShowCancellationDate] = useState(
    proposal.tripBuilder?.showCancellationDate ?? proposal.hotels[0]?.showCancellationDate ?? true
  );
  const [defaultCancellationDateDelta, setDefaultCancellationDateDelta] = useState(
    String(
      proposal.tripBuilder?.cancellationDateDelta ?? proposal.hotels[0]?.cancellationDateDelta ?? DEFAULT_CANCELLATION_DATE_DELTA
    )
  );
  const [displayTotalDelta, setDisplayTotalDelta] = useState(
    () => proposal.tripBuilder?.displayTotalDelta ?? false,
  );
  const [proposalImage, setProposalImage] = useState(
    () => proposal.tripBuilder?.proposalImage || '',
  );
  useEffect(() => {
    setDefaultShowCancellationDate(
      proposal.tripBuilder?.showCancellationDate ?? proposal.hotels[0]?.showCancellationDate ?? true
    );
    setDefaultCancellationDateDelta(
      String(
        proposal.tripBuilder?.cancellationDateDelta ?? proposal.hotels[0]?.cancellationDateDelta ?? DEFAULT_CANCELLATION_DATE_DELTA
      )
    );
    setDisplayTotalDelta(proposal.tripBuilder?.displayTotalDelta ?? false);
    setProposalImage(proposal.tripBuilder?.proposalImage || '');
  }, [proposal.tripBuilder, proposal.hotels]);

  useEffect(() => {
    if (!proposal.existingProposal) {
      return;
    }
    setLanguage(resolveLanguageFromProposal(proposal.existingProposal.externalParam_1));
  }, [proposal.existingProposal]);
  useEffect(() => {
    proposal.ilPackages.forEach((pac) => {
      pac.rooms?.forEach((room, roomIndex) => {
        room.catalogAdditionalPayments?.forEach((pay: any) => {
          const prevRoomsPaxCount =
            pac.rooms
              ?.filter((_, rcIndex) => rcIndex < roomIndex)
              .reduce((acc: number, curr) => acc + curr.complect.length, 0) || 0;

          const roomStr = pac.rooms?.length! > 1 ? `${t('hotel.room')} ${roomIndex + 1}, ` : '';
          const guestStr = `${t('passenger.guest')} ${pay.paxCode - prevRoomsPaxCount}, `;

          setAdditionalPaymentsStr((prev) => [
            ...prev,
            `${roomStr} ${guestStr} ${pay.value} ${pay.rawPrice}`,
          ]);
        });
      });
    });

    const newHotels = proposal.hotels.map((proposal) => {
      return {
        ...proposal,
        showRooms: true,
      };
    });

    const newDynamic = proposal.dynamic.map((dynamic) => {
      let result = { ...dynamic };
      if (dynamic.hotel) {
        result.hotel = {
          ...dynamic.hotel,
          choosenRoomIds: !dynamic.hotel?.choosenRoomIds?.length
            ? [dynamic.hotel?.roomClasses[0].rcUniqueKey]
            : dynamic.hotel?.choosenRoomIds,
        };
      }
      return result;
    }) as DynamicProposal[];

    onChangeProposal({ ...proposal, hotels: newHotels, dynamic: newDynamic });

    (async () => {
      const newProposal = await checkIfProposalExpired(proposal, t);
      if (newProposal) {
        onChangeProposal(newProposal);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeProposal = async (newProposal: Proposal) => {
    setProposal(newProposal);
  };

  const onClickClearAll = () => {
    onChangeProposal({
      hotels: [],
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: [],
      existingProposal: undefined,
    });
    setComment('');
  };

  const commentRef = useRef(comment);
  const languageRef = useRef(language);
  const templateRef = useRef(template);
  const sortRef = useRef(sort);
  const currencyRef = useRef(currency);
  const proposalRef = useRef(proposal);
  const additionalInfoRef = useRef(additionalInfo);
  commentRef.current = comment;
  languageRef.current = language;
  templateRef.current = template;
  sortRef.current = sort;
  currencyRef.current = currency;
  proposalRef.current = proposal;
  additionalInfoRef.current = additionalInfo;

  useEffect(() => {
    setCurrency(getActiveProposalCurrency(proposal));
  }, [proposal]);

  const withOwnerName = (
    info: TripBuilderAdditionalInfoValues,
    ownerName?: string,
  ): TripBuilderAdditionalInfoValues => {
    const firstName = ownerName?.trim() || info.firstName?.trim() || '';
    const expireDate = info.expireDate?.trim() || getDefaultTripBuilderExpireDate();
    if (firstName === info.firstName && expireDate === info.expireDate) {
      return info;
    }
    return {
      ...info,
      firstName,
      expireDate,
    };
  };

  const ensureHotelRoomsSelected = (current: Proposal): Proposal => {
    let changed = false;
    const hotels = current.hotels.map((hotel) => {
      if (hotel.item.choosenRoomIds?.length) {
        return hotel;
      }
      const firstRoomKey = hotel.item.roomClasses?.[0]?.rcUniqueKey;
      if (!firstRoomKey) {
        return hotel;
      }
      changed = true;
      return {
        ...hotel,
        item: {
          ...hotel.item,
          choosenRoomIds: [firstRoomKey],
        },
      };
    });
    const dynamic = current.dynamic.map((item) => {
      if (!item.hotel || item.hotel.choosenRoomIds?.length) {
        return item;
      }
      const firstRoomKey = item.hotel.roomClasses?.[0]?.rcUniqueKey;
      if (!firstRoomKey) {
        return item;
      }
      changed = true;
      return {
        ...item,
        hotel: {
          ...item.hotel,
          choosenRoomIds: [firstRoomKey],
        },
      };
    });
    return changed ? { ...current, hotels, dynamic } : current;
  };

  const onClickSave = async (override?: {
    additionalInfo?: TripBuilderAdditionalInfoValues;
    proposalSettings?: TripBuilderAdditionalInfoProposalSettings;
    proposal?: Proposal;
  }) => {
    const comment = commentRef.current;
    const language = languageRef.current;
    const template = templateRef.current;
    const sort = sortRef.current;
    const activeProposal = override?.proposal ?? proposalRef.current;
    const everyHotelHasRoom = activeProposal.hotels.every((hotel) => hotel.item.choosenRoomIds?.length);
    const everyDynamicHasRoom = activeProposal.dynamic.every(
      (item) => !item.hotel || item.hotel.choosenRoomIds?.length,
    );
    if (!everyHotelHasRoom || !everyDynamicHasRoom) {
      showWarning(t('passenger.every_hotel_must_have_at_least_one_room'));
      return;
    }

    const info = override?.additionalInfo ?? additionalInfoRef.current;
    const isValid = validate(tripBuilderAdditionalInfoValidation, info);
    if (!isValid) {
      additionalInfoOpenedByValidationRef.current = true;
      setAdditionalInfoShowValidation(true);
      setShowAdditionalInfoModal(true);
      return;
    }

    setLoadingCreate(true);

    const nextShowCancellationDate =
      override?.proposalSettings?.showCancellationDate ?? defaultShowCancellationDate;
    const nextDisplayTotalDelta =
      override?.proposalSettings?.displayTotalDelta ?? displayTotalDelta;
    const nextCancellationDateDelta =
      override?.proposalSettings?.cancellationDateDelta ?? defaultCancellationDateDelta;
    const nextProposalImage = override?.proposalSettings?.proposalImage ?? proposalImage;

    const token = await handleRecapcha();
    if (!token) {
      setLoadingCreate(false);
      return;
    }

    const proposalToSave = await applyProposalCurrency(
      activeProposal,
      getActiveProposalCurrency(activeProposal),
    );

    const hotelMissingRooms = proposalToSave.hotels.some(
      (hotel) => !hotel.item?.choosenRoomIds?.length,
    );
    const dynamicMissingRooms = proposalToSave.dynamic.some(
      (item) => item.hotel && !item.hotel?.choosenRoomIds?.length,
    );
    if (hotelMissingRooms || dynamicMissingRooms) {
      showWarning(t('passenger.every_hotel_must_have_at_least_one_room'));
      setLoadingCreate(false);
      return;
    }

    const reportList = getReportList(proposalToSave, {
      showCancellationDate: nextShowCancellationDate,
      cancellationDateDelta: +nextCancellationDateDelta,
      language,
    });

    const inputData = {
      user: user?.proposalUserInfo,
      branchLogo: user?.branch?.logoName || '',
      nameFromFax: user?.nameFromFax,
      template,
      language,
      sort,
      isTripBuilder: false,
      proposalImage: nextProposalImage,
      claudeHotelInfoConfig: JSON.stringify(
        Object.fromEntries(
          proposalToSave.hotels
            .filter((hotel) => hotel.claudeHotelInfoConfig)
            .map((hotel) => [hotel.item.itemCode, hotel.claudeHotelInfoConfig]),
        ),
      ),
      hotelInfoConfig: JSON.stringify(
        Object.fromEntries(
          proposalToSave.hotels
            .filter((hotel) => hotel.hotelInfoConfig)
            .map((hotel) => [hotel.item.itemCode, hotel.hotelInfoConfig]),
        ),
      ),
    };
    const params: CreateProposalParams = {
      comments: comment,
      reportsList: reportList,
      recaptchaToken: token,
      inputData: JSON.stringify(inputData),
      description: getProposalDescription(proposalToSave),
      displayTotalDelta: nextDisplayTotalDelta,
      activeLink: true,
      ...buildTripBuilderAdditionalInfoParams(info),
    };

    const response = await createProposal(params);

    const emptyProposal = { hotels: [], flights: [], dynamic: [], ilPackages: [], transfers: [] };
    if (response && !response.hasErrors && !response.sessionExpired) {
      showSuccess(t('proposal.created'));

      if (!response?.proposal?.listOfProducts?.length) {
        addLogs(
          `proposal_empty_created ${response.proposal?.id} created but no products` +
          JSON.stringify(response)
        );
      }

      if (response.proposal) {
        onChangeProposal({
          ...emptyProposal,
          existingProposal: response.proposal,
        });
      } else {
        onChangeProposal(emptyProposal);
        pendingShareRef.current = null;
      }
    } else if (response?.sessionExpired) {
      onChangeProposal(emptyProposal);
      pendingShareRef.current = null;
      showError(t('session_expired_try_again'));
    } else if (response?.hasErrors) {
      console.log(response?.errorMessage);
      pendingShareRef.current = null;
      showError(t('something_went_wrong'));
    } else {
      pendingShareRef.current = null;
      showError(t('something_went_wrong'));
    }

    setLoadingCreate(false);
  };

  useEffect(() => {
    if (!proposal.existingProposal || !pendingShareRef.current) {
      return;
    }
    const share = pendingShareRef.current;
    pendingShareRef.current = null;
    const timer = window.setTimeout(() => {
      enqueueProposalUi([{ kind: 'share', ...share }]);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [proposal.existingProposal]);

  const onCloseEditPrice = (type?: 'p' | 'v' | 'e', value?: number, isAll?: boolean) => {
    setShowEditPrice(false);
    setSelectedProduct('');
    setSelectedIndex(-1);
    setSelectedRcUniqueKey(undefined);

    if (!type) {
      return;
    }

    let newProposal = { ...proposal };
    if (isAll) {
      newProposal.dynamic = newProposal.dynamic.map((d) => {
        return {
          ...d,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.flights = newProposal.flights.map((f) => {
        return {
          ...f,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.transfers = newProposal.transfers.map((t) => {
        return {
          ...t,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.ilPackages = newProposal.ilPackages.map((p) => {
        return {
          ...p,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.hotels = newProposal.hotels.map((h) => {
        return {
          ...h,
          item: {
            ...h.item,
            roomClasses: h.item.roomClasses.map((room: any) => ({
              ...room,
              viewType: type,
              viewValue: value,
            })),
          },
        };
      });
      if (newProposal.existingProposal) {
        newProposal.existingProposal = {
          ...newProposal.existingProposal,
          listOfProducts: newProposal.existingProposal?.listOfProducts?.map((p) => ({
            ...p,
            viewType: type,
            viewValue: value,
          })) as any,
        };
      }
    } else if (selectedProduct in proposal && selectedProduct !== 'existingProposal') {
      const productKey = selectedProduct as ProposalProductKey;
      if (newProposal[productKey] && newProposal[productKey][selectedIndex]) {
        if (productKey === 'hotels' && selectedRcUniqueKey) {
          const selectedHotel = newProposal.hotels[selectedIndex];
          if (selectedHotel) {
            selectedHotel.item.roomClasses = selectedHotel.item.roomClasses.map((room: any) =>
              room.rcUniqueKey === selectedRcUniqueKey
                ? { ...room, viewType: type, viewValue: value }
                : room
            );
          }
        } else if (productKey !== 'hotels') {
          newProposal[productKey][selectedIndex].viewType = type;
          newProposal[productKey][selectedIndex].viewValue = value;
        }
      }
    }

    setProposal(newProposal);
  };

  const areSomeProposal = () => {
    return !!(
      proposal.hotels.length ||
      proposal.flights.length ||
      proposal.dynamic.length ||
      proposal.transfers.length ||
      proposal.existingProposal?.listOfProducts.length ||
      proposal.ilPackages.length
    );
  };

  const onClickOpenPopup = (product: string, index: number, rcUniqueKey?: string) => {
    setShowEditPrice(true);
    setSelectedProduct(product);
    setSelectedIndex(index);
    setSelectedRcUniqueKey(rcUniqueKey);
  };

  const getInitialProduct = () => {
    if (selectedIndex === -1) return;

    const result =
      proposal[selectedProduct as ProposalProductKey][selectedIndex];

    return result;
  };

  const getOpenData = () => {
    if (
      !selectedProduct ||
      selectedIndex === -1 ||
      selectedProduct === 'existingProposal' ||
      !proposal[selectedProduct as ProposalProductKey] ||
      !proposal[selectedProduct as ProposalProductKey][selectedIndex]
    ) {
      return undefined;
    }

    if (selectedProduct === 'hotels') {
      if (!selectedRcUniqueKey) {
        return undefined;
      }
      const selectedRoom = proposal.hotels[selectedIndex]?.item?.roomClasses?.find(
        (room: any) => room.rcUniqueKey === selectedRcUniqueKey
      );
      return {
        value: selectedRoom?.viewValue?.toString() || '',
        type: selectedRoom?.viewType || 'v',
      };
    }

    const selectedItem = proposal[selectedProduct as ProposalProductKey][selectedIndex] as Exclude<
      Proposal[ProposalProductKey][number],
      { item: any }
    >;

    return {
      value: selectedItem.viewValue + '',
      type: selectedItem.viewType as any,
    };
  };

  const onClickSubmit = () => {
    void onClickSave();
  };

  useProposalUi(
    (intent) =>
      listenToAssistant &&
      (intent.kind === 'comment' ||
        intent.kind === 'clear-comment' ||
        intent.kind === 'customize' ||
        intent.kind === 'create' ||
        intent.kind === 'save' ||
        intent.kind === 'open-price'),
    (intent) => {
      if (intent.kind === 'comment') {
        const next =
          intent.mode === 'append' && commentRef.current.trim()
            ? `${commentRef.current.trim()} ${intent.text.trim()}`.trim()
            : intent.text;
        commentRef.current = next;
        setComment(next);
      }
      if (intent.kind === 'clear-comment') {
        commentRef.current = '';
        setComment('');
      }
      if (intent.kind === 'customize') {
        if (intent.language) {
          languageRef.current = intent.language;
          setLanguage(intent.language);
        }
        if (intent.template) {
          templateRef.current = intent.template;
          setTemplate(intent.template);
        }
        if (intent.sort) {
          sortRef.current = intent.sort;
          setSort(intent.sort);
        }
        if (intent.currency) {
          const nextCurrency = intent.currency.toLowerCase();
          currencyRef.current = nextCurrency;
          setCurrency(nextCurrency);
          sessionStorage.setItem('displayCurrencyHotels', nextCurrency);
          void applyProposalCurrency(proposalRef.current, nextCurrency).then((updated) => {
            proposalRef.current = updated;
            setProposal(updated);
          });
        }
      }
      if (intent.kind === 'create' || intent.kind === 'save') {
        if (intent.kind === 'create' && intent.thenShare) {
          pendingShareRef.current = intent.thenShare;
        }
        if (proposalRef.current.existingProposal) {
          setLoadingUpdate(true);
          setEditDate(new Date());
          if (pendingShareRef.current) {
            const share = pendingShareRef.current;
            pendingShareRef.current = null;
            window.setTimeout(() => {
              enqueueProposalUi([{ kind: 'share', ...share }]);
            }, 0);
          }
        } else {
          const ownerName =
            intent.kind === 'create' ? intent.ownerName?.trim() : undefined;
          if (!ownerName && !additionalInfoRef.current.firstName?.trim()) {
            pendingShareRef.current = null;
            additionalInfoOpenedByValidationRef.current = true;
            setAdditionalInfoShowValidation(true);
            setShowAdditionalInfoModal(true);
            return;
          }
          const next = ensureHotelRoomsSelected(proposalRef.current);
          if (next !== proposalRef.current) {
            proposalRef.current = next;
            setProposal(next);
          }
          const info = {
            ...withOwnerName(additionalInfoRef.current, ownerName),
            ...(intent.kind === 'create' && intent.email != null ? { email: intent.email } : {}),
            ...(intent.kind === 'create' && intent.password != null
              ? { password: intent.password }
              : {}),
            ...(intent.kind === 'create' && intent.expireDate
              ? { expireDate: intent.expireDate }
              : {}),
          };
          if (info !== additionalInfoRef.current) {
            additionalInfoRef.current = info;
            setAdditionalInfo(info);
          }
          if (!info.firstName?.trim()) {
            pendingShareRef.current = null;
            additionalInfoOpenedByValidationRef.current = true;
            setAdditionalInfoShowValidation(true);
            setShowAdditionalInfoModal(true);
            return;
          }
          const proposalSettings =
            intent.kind === 'create'
              ? {
                  showCancellationDate:
                    typeof intent.showCancellationDate === 'boolean'
                      ? intent.showCancellationDate
                      : defaultShowCancellationDate,
                  displayTotalDelta:
                    typeof intent.displayTotalDelta === 'boolean'
                      ? intent.displayTotalDelta
                      : displayTotalDelta,
                  cancellationDateDelta:
                    intent.cancellationDateDelta != null
                      ? String(intent.cancellationDateDelta)
                      : defaultCancellationDateDelta,
                  proposalImage: intent.proposalImage ?? proposalImage,
                }
              : undefined;
          if (proposalSettings) {
            setDefaultShowCancellationDate(proposalSettings.showCancellationDate);
            setDisplayTotalDelta(proposalSettings.displayTotalDelta);
            setDefaultCancellationDateDelta(proposalSettings.cancellationDateDelta);
            setProposalImage(proposalSettings.proposalImage);
          }
          void onClickSave({ proposal: next, additionalInfo: info, proposalSettings });
        }
      }
      if (intent.kind === 'open-price') {
        onClickOpenPopup(intent.product, intent.index, intent.roomKey);
      }
    },
  );

  const onExistingProposalSaved = () => {
    showSuccess(t('proposal.updated', { defaultValue: 'Proposal updated' }));
  };

  return (
    <div className={`pr-edit pr-edit--${template}`}>
      <div className="pr-page-body">
        {!areSomeProposal() && <div className="text-center flex-center mt-20 mb-20">{t('proposal.no')}</div>}

        {areSomeProposal() && (
          <Fragment>
            {!!proposal.hotels.length && (
              <Fragment>
                <div className="pr-section-title pr-section-title--hotels mb-15">
                  <div className="pr-section-title-row text-bold">{t('hotel.s')}</div>
                  <div className="pr-hotels-settings">
                    <div className="pr-hotels-settings-delta">
                      <div className="text-sm pr-hotels-settings-delta-label">
                        {t('proposal.cancellation_date_delta')}
                      </div>
                      <SelectCustom
                        options={[0, 1, 2, 3, 4, 5].map((n) => ({
                          label: String(n),
                          value: String(n),
                        }))}
                        value={defaultCancellationDateDelta}
                        onChange={(value) => {
                          setDefaultCancellationDateDelta(value);
                          onChangeProposal({
                            ...proposal,
                            tripBuilder: {
                              ...proposal.tripBuilder,
                              rows: proposal.tripBuilder?.rows || {},
                              cancellationDateDelta: +value,
                              showCancellationDate: defaultShowCancellationDate,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="pr-hotels-settings-checkboxes">
                      <Checkbox
                        checked={defaultShowCancellationDate}
                        label={t('proposal.show_cancellation_date')}
                        className={'mb-5 ' + (defaultShowCancellationDate ? 'text-bold' : '')}
                        onChange={(value) => {
                          setDefaultShowCancellationDate(value);
                          onChangeProposal({
                            ...proposal,
                            tripBuilder: {
                              ...proposal.tripBuilder,
                              rows: proposal.tripBuilder?.rows || {},
                              showCancellationDate: value,
                              cancellationDateDelta: +defaultCancellationDateDelta,
                            },
                          });
                        }}
                      />
                      <Checkbox
                        checked={proposal.hotels[0].displayPrice}
                        label={t('proposal.show_price')}
                        className={proposal.hotels[0].displayPrice ? 'text-bold' : ''}
                        onChange={(value) => {
                          onChangeProposal({
                            ...proposal,
                            hotels: proposal.hotels.map((hotel) => {
                              return { ...hotel, displayPrice: value };
                            }),
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                {proposal.hotels.map((hot, index) => (
                  <Hotel2Proposal
                    key={index}
                    proposal={proposal}
                    hotel={hot.item}
                    onChangeProposal={onChangeProposal}
                    hotelIndex={index}
                    openPopup={onClickOpenPopup}
                    defaultShowCancellationDate={defaultShowCancellationDate}
                    defaultCancellationDateDelta={+defaultCancellationDateDelta}
                    assistantOwner={listenToAssistant}
                    tripBuilderMode={tripBuilderMode}
                  />
                ))}
              </Fragment>
            )}

            {!!proposal.flights.length && (
              <div className="pr-section">
                <div className="pr-section-title flex-between">
                  <span className="text-bold">{t('flight.s')}</span>
                  <Checkbox
                    checked={proposal.flights[0].displayPrice}
                    label={t('proposal.show_price')}
                    className={proposal.flights[0].displayPrice ? 'text-bold' : ''}
                    onChange={(val) =>
                      onChangeProposal({
                        ...proposal,
                        flights: proposal.flights.map((f) => ({ ...f, displayPrice: val })),
                      })
                    }
                  />
                </div>
                <div className="pr-data-table">
                  <table className="pr-table">
                    <thead>
                      <tr>
                        <th>{t('flight.airline')}</th>
                        <th>{t('flight.flight')}</th>
                        <th>{t('flight.dep_time')}</th>
                        <th>{t('flight.dep_airport')}</th>
                        <th>{t('flight.arr_time')}</th>
                        <th>{t('flight.arr_airport')}</th>
                        <th>{t('flight.aircraft')}</th>
                        <th>{t('flight.elapsed_time')}</th>
                        <th>
                          {t('original_price')} / {t('price')}
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.flights.map((flight, index) => (
                        <FlightProposal
                          key={flight.item.id + index}
                          flight={flight.item}
                          proposal={proposal}
                          setProposal={onChangeProposal}
                          proposalIndex={index}
                          openPopup={onClickOpenPopup}
                          assistantOwner={listenToAssistant}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!!proposal.dynamic.length && (
              <div className="pr-section pr-dynamic">
                <div className="pr-section-title flex-between mb-10">
                  <span className="text-bold">{t('dyn.packages')}</span>
                  <Checkbox
                    checked={proposal.dynamic[0].displayPrice}
                    label={t('proposal.show_price')}
                    className={proposal.dynamic[0].displayPrice ? 'text-bold' : ''}
                    onChange={(value) => {
                      onChangeProposal({
                        ...proposal,
                        dynamic: proposal.dynamic.map((d) => ({ ...d, displayPrice: value })),
                      });
                    }}
                  />
                </div>
                {proposal.dynamic.map((dynamic, index) => (
                  <Fragment key={index + dynamic.searchToken}>
                    <div className="pr-section-title pr-section-title--dark flex-between">
                      <span className="text-bold">
                        {[!!dynamic.flight, !!dynamic.hotel, !!dynamic.attraction].filter(Boolean)
                          .length > 1 && <span>{t('proposal.dynamic')} - </span>}

                        {[
                          dynamic.flight ? 'flight.flight' : '',
                          dynamic.hotel ? 'hotel.hotel' : '',
                          dynamic.attraction ? 'dyn.ticket' : '',
                        ]
                          .filter(Boolean)
                          .map((tr) => t(tr))
                          .join(' & ')}
                      </span>

                      <div>
                        <div className="flex-center" style={{ justifyContent: 'flex-end' }}>
                          {!!dynamic.viewValue && (
                            <span className="text-contrast">
                              {formatPriceCurrency(dynamic.price, getCurrency(dynamic.currency))} /{' '}
                            </span>
                          )}
                          <span
                            className="text-bold text-lg text-contrast text-nowrap cur-poi"
                            onClick={() => onClickOpenPopup('dynamic', index)}
                          >
                            {formatPriceProposal(
                              dynamic.price,
                              dynamic.currency,
                              dynamic.viewType,
                              dynamic.viewValue
                            )}
                            <FontAwesomeIcon icon={faPencil} className="ml-5" size="xs" />
                          </span>

                          <FontAwesomeIcon
                            icon={faTrash}
                            className="ml-10 cur-poi"
                            onClick={() => {
                              setProposal({
                                ...proposal,
                                dynamic: proposal.dynamic.filter((_, i) => i !== index),
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {!!dynamic.flight && (
                      <div className="pr-data-table">
                        <table className="pr-table" key={index + dynamic.searchToken}>
                          <thead>
                            <tr>
                              <th>{t('flight.airline')}</th>
                              <th>{t('flight.flight')}</th>
                              <th>{t('flight.dep_time')}</th>
                              <th>{t('flight.dep_airport')}</th>
                              <th>{t('flight.arr_time')}</th>
                              <th>{t('flight.arr_airport')}</th>
                              <th>{t('flight.aircraft')}</th>
                              <th>{t('flight.elapsed_time')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <FlightProposal
                              flight={dynamic.flight}
                              proposal={proposal}
                              setProposal={onChangeProposal}
                              proposalIndex={index}
                              dynamic={dynamic}
                              openPopup={onClickOpenPopup}
                              assistantOwner={listenToAssistant}
                            />
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mb-20"></div>
                    {!!dynamic.hotel && (
                      <Hotel2Proposal
                        proposal={proposal}
                        hotel={dynamic.hotel}
                        onChangeProposal={onChangeProposal}
                        hotelIndex={index}
                        dynamic={dynamic}
                        openPopup={onClickOpenPopup}
                        assistantOwner={listenToAssistant}
                        tripBuilderMode={tripBuilderMode}
                      />
                    )}
                    {!!dynamic.attraction && (
                      <TicketProposal
                        ticket={dynamic.attraction}
                        price={
                          dynamic.flight ? '' : formatPriceProposal(dynamic.price, dynamic.currency)
                        }
                        dynamicIndex={index}
                      />
                    )}
                    {index !== proposal.dynamic.length - 1 && (
                      <div className="divider mb-20 mt-20"></div>
                    )}
                  </Fragment>
                ))}
              </div>
            )}

            {!!proposal.transfers.length && (
              <div className="pr-section">
                <div className="pr-section-title flex-between">
                  <span className="text-bold">{t('transfer.s')}</span>
                  <Checkbox
                    checked={proposal.transfers[0].displayPrice}
                    label={t('proposal.show_price')}
                    className={proposal.transfers[0].displayPrice ? 'text-bold' : ''}
                    onChange={(value) => {
                      onChangeProposal({
                        ...proposal,
                        transfers: proposal.transfers.map((d) => ({ ...d, displayPrice: value })),
                      });
                    }}
                  />
                </div>

                <div className="pr-data-table">
                  <table className="pr-table">
                    <thead>
                      <tr className="text-start">
                        <th>#</th>
                        <th>{t('passenger.s')}</th>
                        <th>{t('transfer.pick_up')}</th>
                        <th className="text-nowrap">
                          {t('transfer.from')} - {t('transfer.to')}
                        </th>
                        <th>{t('transfer.vehicle')}</th>
                        <th colSpan={2}>
                          {t('original_price')} / {t('price')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.transfers.map((tran, index) => (
                        <TrnProposal
                          key={index + tran.searchToken}
                          transfer={tran}
                          proposal={proposal}
                          setProposal={onChangeProposal}
                          proposalIndex={index}
                          openPopup={onClickOpenPopup}
                          assistantOwner={listenToAssistant}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!!proposal.ilPackages.length && (
              <div className="pr-section">
                <div className="pr-section-title flex-between">
                  <span className="text-bold">{t('package.s')}</span>
                  <Checkbox
                    checked={proposal.ilPackages[0].displayPrice}
                    label={t('proposal.show_price')}
                    className={proposal.ilPackages[0].displayPrice ? 'text-bold' : ''}
                    onChange={(value) => {
                      onChangeProposal({
                        ...proposal,
                        ilPackages: proposal.ilPackages.map((d) => ({
                          ...d,
                          displayPrice: value,
                        })),
                      });
                    }}
                  />
                </div>
                <div className="pr-data-table">
                  {proposal.ilPackages.map((pac, index) => (
                    <ILPackageProposal
                      key={index}
                      pac={pac}
                      proposal={proposal}
                      setProposal={onChangeProposal}
                      proposalIndex={index}
                      openPopup={onClickOpenPopup}
                      additionalPaymentsStr={additionalPaymentsStr}
                      assistantOwner={listenToAssistant}
                    />
                  ))}
                </div>
              </div>
            )}
            {!!proposal.existingProposal && (
              <ProposalEdit
                editDate={editDate}
                onUpdatingChange={setLoadingUpdate}
                template={template}
                language={language}
                sort={sort}
                onProposalSaved={onExistingProposalSaved}
                assistantOwner={listenToAssistant}
              />
            )}

            {!proposal.existingProposal && (
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                className="form-control form-control-textarea"
                placeholder={t('proposal.write_comments')}
                style={{ direction: checkAHAVI(comment) ? 'rtl' : 'ltr' }}
              ></textarea>
            )}
          </Fragment>
        )}
      </div>
      <div className="flex-between pr-page-footer">
        <div className="pr-page-footer-customize">
          <ProposalCustomize
            onChange={(t) => {
              setTemplate(t.template);
              setLanguage(t.language);
              setSort(t.sort);
            }}
            hideWrapper
            config={{ template, language, sort, currency }}
          />
        </div>
        {!!proposal.existingProposal && (
          <div className="pr-page-footer-actions">
            <ProposalActions proposal={proposal.existingProposal} assistantOwner={listenToAssistant} />
          </div>
        )}
        <div className="pr-page-footer-btns">
          {areSomeProposal() && (
            <Button
              title={t('proposal.clear_all')}
              className="btn btn-default"
              onClick={onClickClearAll}
            />
          )}
          {areSomeProposal() && !proposal.existingProposal && (
            <Button
              title={t('proposal.create')}
              onClick={onClickSubmit}
              disabled={loadingCreate}
            />
          )}
          {!!proposal.existingProposal && (
            <Button
              title={t('proposal.save')}
              onClick={() => {
                setLoadingUpdate(true);
                setEditDate(new Date());
              }}
              disabled={loadingCreate || loadingUpdate}
            />
          )}
        </div>
      </div>
      <EditPriceModal
        onClose={onCloseEditPrice}
        initialProduct={{
          product: getInitialProduct(),
          rcUniqueKey: selectedRcUniqueKey,
        }}
        openData={getOpenData()}
        isOpen={showEditPrice}
      />

      {showAdditionalInfoModal && (
        <TripBuilderAdditionalInfoModal
          values={additionalInfo}
          showValidation={additionalInfoShowValidation}
          close={() => {
            additionalInfoOpenedByValidationRef.current = false;
            pendingAdditionalInfoRef.current = null;
            setShowAdditionalInfoModal(false);
            setAdditionalInfoShowValidation(false);
          }}
          onSave={(values) => {
            pendingAdditionalInfoRef.current = values;
            setAdditionalInfo(values);
            setAdditionalInfoShowValidation(false);
          }}
          proposalSettings={{
            showCancellationDate: defaultShowCancellationDate,
            displayTotalDelta,
            cancellationDateDelta: defaultCancellationDateDelta,
            proposalImage,
          }}
          onProposalSettingsSave={(settings) => {
            setDefaultShowCancellationDate(settings.showCancellationDate);
            setDisplayTotalDelta(settings.displayTotalDelta);
            setDefaultCancellationDateDelta(settings.cancellationDateDelta);
            setProposalImage(settings.proposalImage);
            onChangeProposal({
              ...proposal,
              tripBuilder: {
                ...proposal.tripBuilder,
                rows: proposal.tripBuilder?.rows || {},
                showCancellationDate: settings.showCancellationDate,
                cancellationDateDelta: +settings.cancellationDateDelta,
                displayTotalDelta: settings.displayTotalDelta,
                proposalImage: settings.proposalImage,
              },
            });

            if (!additionalInfoOpenedByValidationRef.current) {
              return;
            }

            additionalInfoOpenedByValidationRef.current = false;
            const info = pendingAdditionalInfoRef.current ?? additionalInfo;
            pendingAdditionalInfoRef.current = null;
            void onClickSave({ additionalInfo: info, proposalSettings: settings });
          }}
        />
      )}

      {loadingCreate && <LoaderMain systemProducts={'Flights'} />}
    </div>
  );
};

export default withErrorBoundary(ProposalNew, 'ProposalNew');
